import type { ProgrammaticLandingPageConfig } from '../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from './storageSizeFacts'

export const KWH_15_SPEICHER_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '15-kwh-speicher-kosten',
  lang: 'de',
  topicId: 'kwh-speicher-15',
  title: '15 kWh Batteriespeicher: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet ein 15-kWh-Batteriespeicher? Für wen sich diese Speichergröße eignet, ' +
    'Eigenverbrauchs-Richtwerte und kostenloser Preisvergleich für Anlage + Speicher.',
  keywords: [
    '15 kWh Speicher Kosten',
    'Batteriespeicher 15 kWh Preis',
    '15 kWh Solarspeicher',
    'Was kostet ein 15 kWh Speicher',
  ],
  eyebrow: 'Speichergröße 15 kWh',
  h1: '15 kWh Batteriespeicher: Kosten und Richtwerte',
  intro:
    'Ein 15-kWh-Speicher lohnt sich vor allem für Haushalte mit Wärmepumpe oder regelmäßigem ' +
    `E-Auto-Laden, kombiniert mit einer ${STORAGE_REFERENCE_KWP}-kWp-Referenzanlage. Hier finden ` +
    'Sie Richtwerte zur passenden Haushaltsgröße, zum Eigenverbrauch mit Speicher und direkt ' +
    'darunter einen Rechner mit dem für diese Kombination vorbelegten Durchschnittspreis aus ' +
    'echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 15 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich ein 15-kWh-Speicher?' },
    {
      type: 'paragraph',
      text: 'Richtwert: 15 kWh passen zu Haushalten mit Wärmepumpe oder regelmäßigem E-Auto-Laden. ' +
        'Bei beiden Großverbrauchern ' +
        'gleichzeitig kann sich sogar die nächstgrößere 20-kWh-Stufe lohnen.',
    },
    { type: 'heading', text: 'Eigenverbrauch mit Batteriespeicher' },
    {
      type: 'paragraph',
      text:
        `Ohne Speicher liegt der Eigenverbrauchsanteil bei einer ${STORAGE_REFERENCE_KWP}-kWp-Anlage ` +
        `typischerweise bei rund ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Mit Batteriespeicher ` +
        `steigt dieser Anteil im Rechenmodell um ${SELF_CONSUMPTION_BATTERY_BONUS * 100} ` +
        `Prozentpunkte auf rund ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - Wärmepumpe und E-Auto ` +
        'bringen im Modell jeweils einen zusätzlichen Bonus, bis zu einer Obergrenze von 85 %. Eine ' +
        'genaue, auf Ihren Verbrauch zugeschnittene Berechnung liefert der PV-Rechner.',
    },
    { type: 'heading', text: 'Notstromfähigkeit' },
    {
      type: 'paragraph',
      text:
        'Mit 15 kWh lassen sich über eine Notstrom-Backup-Box in der Regel auch größere Teile des ' +
        'Haushalts über einen längeren Zeitraum versorgen - wie lange genau, hängt vom tatsächlichen ' +
        'Verbrauch der angeschlossenen Geräte ab. Ob und in welchem Umfang ein Angebot ' +
        'Notstromfähigkeit einschließt, ist je Handwerker unterschiedlich - gezielt beim Angebot ' +
        'nachfragen.',
    },
    { type: 'heading', text: 'Speicherkosten und Förderung' },
    {
      type: 'paragraph',
      text:
        'Speicherkosten schwanken je nach Hersteller und Installationsaufwand stark - Marktrecherche ' +
        `2026 zeigt eine Spanne von etwa ${BATTERY_COST_PER_KWH_RANGE_EUR.min} bis ` +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('de-DE')} € pro kWh - bei 15 kWh ` +
        'entsprechend spürbar im Gesamtbudget. Speicherspezifische Förderungen gibt es regional ' +
        'unterschiedlich (z. B. Berliner Landesprogramm mit Zuschuss für Batteriespeicher) - ' +
        'Details im Förder-Überblick, eine genaue Berechnung für Ihre Kombination liefert der ' +
        'Rechner unten.',
    },
  ],
  faq: [
    {
      q: 'Brauche ich 15 kWh, wenn ich eine Wärmepumpe habe?',
      a: 'Eine Wärmepumpe erhöht den möglichen Eigenverbrauchsanteil im Rechenmodell - 15 kWh ist dafür eine gängige Größenordnung. Die exakt passende Größe hängt vom tatsächlichen Heizverbrauch ab, siehe PV-Rechner.',
    },
    {
      q: 'Ist 15 kWh für Haushalte ohne Wärmepumpe oder E-Auto überdimensioniert?',
      a: 'In vielen Fällen ja - ohne diese Großverbraucher reicht meist 10 oder 12 kWh aus, um einen Großteil des möglichen Eigenverbrauchs abzudecken.',
    },
    {
      q: 'Mit welcher Anlagengröße wird ein 15-kWh-Speicher meist kombiniert?',
      a: `Auf dieser Seite als Referenz eine ${STORAGE_REFERENCE_KWP}-kWp-Anlage, in der Praxis häufig auch mit 15 kWp - eine genaue Empfehlung für Ihr Dach liefert der PV-Rechner.`,
    },
    {
      q: 'Reicht 15 kWh, um über Nacht komplett autark zu sein?',
      a: 'Ja, das reicht in der Regel selbst bei überdurchschnittlichem Verbrauch, z. B. mit E-Auto-Ladung oder Wärmepumpe. Eine genaue, auf Ihren tatsächlichen Verbrauch zugeschnittene Einschätzung liefert der PV-Rechner.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '12 kWh Batteriespeicher: Kosten & Richtwerte', href: '/12-kwh-speicher-kosten' },
    { label: '20 kWh Batteriespeicher: Kosten & Richtwerte', href: '/20-kwh-speicher-kosten' },
    { label: '15 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/15-kwp-photovoltaik-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
